<template>
    <div>
        <form @submit.prevent="saveFollowUp">
            <div class="form-group">
                <label>Nama Follow Up <span class="text-danger">*</span></label>
                <input type="text" v-model="form.name" class="form-control" required />
            </div>

            <div class="form-group">
                <label>Tag Pemicu <span class="text-danger">*</span></label>
                <input type="text" v-model="form.trigger_tag" class="form-control" placeholder="contoh: belum_bayar, sudah_bayar, tanya_harga" required />
                <small class="form-text text-muted">Tag yang akan memicu follow up ini. Contoh: belum_bayar, sudah_bayar, tanya_harga, leads_dingin, closing</small>
            </div>

            <div class="form-group">
                <label>Status</label>
                <select v-model.number="form.is_active" class="form-control">
                    <option :value="1">Aktif</option>
                    <option :value="0">Nonaktif</option>
                </select>
            </div>

            <div class="form-group">
                <label>Prioritas</label>
                <div>
                    <span v-for="n in 5" :key="n" @click="form.priority = (form.priority === n ? 0 : n)"
                        :style="{ cursor: 'pointer', fontSize: '24px', color: n <= form.priority ? '#f5a623' : '#d0d0d0', marginRight: '2px' }"
                        :title="n + ' bintang'">★</span>
                    <span class="ml-2 text-muted small">{{ form.priority ? form.priority + ' / 5' : 'Belum diset' }}</span>
                </div>
                <small class="form-text text-muted">
                    <i class="fas fa-info-circle"></i>
                    Set prioritas follow up (1-5 bintang). Klik bintang yang sama untuk reset. Berguna untuk mengurutkan & menentukan follow up paling penting.
                </small>
            </div>

            <div class="form-group">
                <label>Tanggal Berakhir (Opsional)</label>
                <input type="datetime-local" v-model="form.expired_at_local" class="form-control" />
                <small class="form-text text-muted">
                    <i class="fas fa-info-circle"></i>
                    Kosongkan jika follow up ingin berjalan selamanya. Jika diisi, flow akan otomatis berhenti di tanggal &amp; waktu tersebut. Kontak yang sedang dalam flow akan di-set <code>completed</code>.
                </small>
            </div>

            <div class="form-group">
                <label>Steps <span class="text-danger">*</span></label>
                <div v-for="(step, index) in form.steps" :key="index" class="card mb-3" style="border-radius: 2px; border: 2px solid #dee2e6;">
                    <div class="card-header d-flex justify-content-between">
                        <span>Step {{ index + 1 }}</span>
                        <button type="button" @click="removeStep(index)" class="btn btn-sm btn-danger">
                            <i class="fas fa-trash"></i> Hapus
                        </button>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <div class="col-md-3">
                                <label>Urutan</label>
                                <input type="number" v-model.number="step.step_order" class="form-control" min="0" required />
                            </div>
                            <div class="col-md-3">
                                <label>Tipe Delay</label>
                                <select v-model="step.delay_type" @change="onDelayTypeChange(step)" class="form-control" required>
                                    <option value="minutes">Menit</option>
                                    <option value="hours">Jam</option>
                                    <option value="days">Hari</option>
                                    <option value="fixed_time">Jam Tetap (HH:MM)</option>
                                </select>
                            </div>
                            <div class="col-md-3" v-if="step.delay_type === 'fixed_time'">
                                <label>Hari lagi</label>
                                <input type="number" min="0" class="form-control" :value="Math.floor((step.delay_value || 0) / 1440)" @input="step.delay_value = (parseInt($event.target.value || '0') || 0) * 1440 + ((step.delay_value || 0) % 1440)" />
                                <small class="form-text text-muted">0 = jam tsb berikutnya (hari ini/besok). Mis. 3 = 3 hari lagi.</small>
                            </div>
                            <div class="col-md-3" v-if="step.delay_type === 'fixed_time'">
                                <label>Jam Kirim</label>
                                <input type="time" class="form-control" :value="minutesToHHMM((step.delay_value || 0) % 1440)" @input="step.delay_value = Math.floor((step.delay_value || 0) / 1440) * 1440 + hhmmToMinutes($event.target.value)" required />
                                <small class="form-text text-muted">Dikirim tepat pada jam ini (sesuaikan jam kerja akun)</small>
                            </div>
                            <div class="col-md-3" v-else>
                                <label>Nilai Delay</label>
                                <input type="number" v-model.number="step.delay_value" class="form-control" min="0" required />
                            </div>
                        </div>
                        <br>
                        <div class="form-group">
                            <label>Gunakan Template (Opsional)</label>
                            <select v-model.number="step.template_id" @change="onTemplateChange(index, step.template_id)" class="form-control">
                                <option :value="null">Pilih Template</option>
                                <option v-for="template in templates" :key="template.id" :value="template.id">{{ template.title }}</option>
                            </select>
                            <small class="form-text text-muted">Jika template dipilih, pesan dan media dari template akan digunakan</small>
                        </div>
                        <div v-if="!step.template_id" class="form-group mt-2">
                            <label>Pesan</label>
                            <CustomTextarea v-model="step.message" />
                            <small class="form-text text-muted">
                                <i class="fas fa-info-circle"></i> 
                                Variable yang tersedia: <code>{nama}</code>
                            </small>
                        </div>
                        <div v-if="!step.template_id" class="form-group">
                            <label>Upload Media (Opsional)</label>
                            <input 
                                type="file" 
                                @change="onFileChange(index, $event)" 
                                class="form-control"
                                accept=".jpg,.jpeg,.png,.mp4,.mov"
                            />
                            <small class="form-text text-muted">Format yang didukung: JPG, PNG, MP4, MOV</small>
                            <div v-if="step.media_url" class="mt-2">
                                <img 
                                    v-if="isImage(step.media_url)"
                                    :src="step.media_url" 
                                    alt="Preview" 
                                    style="max-width: 200px; max-height: 200px; border-radius: 8px;"
                                    onerror="this.style.display='none'"
                                />
                                <div v-else class="p-2 bg-light rounded">
                                    <i class="fas fa-file-video"></i> Media terpilih
                                </div>
                                <button 
                                    type="button" 
                                    @click="removeMedia(index)" 
                                    class="btn btn-sm btn-danger mt-2"
                                >
                                    <i class="fas fa-times"></i> Hapus Media
                                </button>
                            </div>
                            <div v-else-if="step.file" class="mt-2">
                                <small class="text-muted">
                                    <i class="fas fa-file"></i> File terpilih: {{ step.file.name }}
                                </small>
                            </div>
                        </div>
                        <div v-else class="form-group mt-2">
                            <small class="form-text text-muted">
                                <i class="fas fa-check-circle text-success"></i> 
                                Template <strong>{{ getTemplateName(step.template_id) }}</strong> dipilih. Pesan dan media akan diisi otomatis dari template saat follow up dijalankan.
                            </small>
                        </div>
                    </div>
                </div>
                <button type="button" @click="addStep" class="btn btn-secondary">
                    <i class="fas fa-plus"></i> Tambah Step
                </button>
            </div>

            <button type="submit" class="btn btn-primary" :disabled="isSaving">
                <i class="fas fa-save"></i> {{ isSaving ? 'Menyimpan...' : 'Simpan Follow Up' }}
            </button>
        </form>
    </div>
</template>

<script>
import CustomTextarea from "@/components/module/CustomTextarea.vue";

export default {
    components: {
        CustomTextarea
    },
    props: {
        isEdit: {
            type: Boolean,
            default: false
        },
        followUpId: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
            form: {
                name: '',
                trigger_tag: '',
                trigger_type: 'tag',
                trigger_condition: null,
                is_active: 1,
                priority: 0,
                expired_at: null,
                expired_at_local: '',
                steps: []
            },
            templates: [],
            isSaving: false,
            isUploading: false,
            uploadingStepIndex: null
        };
    },
    async mounted() {
        await this.loadTemplates();
        if (this.isEdit && this.followUpId) {
            await this.loadFollowUp();
        } else if (this.form.steps.length === 0) {
            this.addStep();
        }
    },
    methods: {
        async loadTemplates() {
            try {
                const response = await this.$store.dispatch("followup/followup_template_list", { page: 1, limit: 100 });
                this.templates = response.data.data.templates || [];
            } catch (err) {
                // Ignore error, templates are optional
            }
        },
        isImage(url) {
            if (!url) return false;
            return /\.(jpg|jpeg|png|gif)$/i.test(url);
        },
        onFileChange(stepIndex, event) {
            const step = this.form.steps[stepIndex];
            const file = event.target.files[0];
            if (file) {
                step.file = file;
            }
        },
        removeMedia(stepIndex) {
            const step = this.form.steps[stepIndex];
            step.file = null;
            step.media_url = null;
            // Reset file input
            const inputs = document.querySelectorAll('input[type="file"]');
            if (inputs[stepIndex]) {
                inputs[stepIndex].value = '';
            }
        },
        getTemplateName(templateId) {
            if (!templateId) return '';
            const template = this.templates.find(t => t.id === templateId);
            return template ? template.title : '';
        },
        async onTemplateChange(stepIndex, templateId) {
            const step = this.form.steps[stepIndex];
            if (templateId) {
                // Clear manual message and media when template is selected
                step.message = '';
                step.media_url = null;
                step.file = null;
                // Reset file input
                const inputs = document.querySelectorAll('input[type="file"]');
                if (inputs[stepIndex]) {
                    inputs[stepIndex].value = '';
                }
            }
        },
        async loadFollowUp() {
            try {
                const response = await this.$store.dispatch("followup/followup_one", this.followUpId);
                const followUp = response.data.data.follow_up;
                this.form = {
                    name: followUp.name,
                    trigger_tag: followUp.trigger_tag || '',
                    trigger_type: followUp.trigger_type || 'tag',
                    trigger_condition: followUp.trigger_condition,
                    is_active: followUp.is_active,
                    priority: followUp.priority || 0,
                    expired_at: followUp.expired_at || null,
                    expired_at_local: followUp.expired_at ? this.toLocalDatetime(followUp.expired_at) : '',
                    steps: followUp.steps.map(step => ({
                        step_order: step.step_order,
                        delay_type: step.delay_type,
                        delay_value: step.delay_value,
                        message: step.message,
                        media_url: step.media_url,
                        button_text: step.button_text,
                        button_url: step.button_url,
                        template_id: step.template_id,
                        file: null
                    }))
                };
            } catch (err) {
                this.$toast.error("Gagal memuat follow up");
            }
        },
        onDelayTypeChange(step) {
            // Saat beralih ke "Jam Tetap", default 09:00 (menit sejak tengah malam).
            if (step.delay_type === 'fixed_time') {
                step.delay_value = 540;
            } else if (step.delay_value > 1439) {
                step.delay_value = 0;
            }
        },
        minutesToHHMM(v) {
            v = parseInt(v) || 0;
            if (v < 0) v = 0;
            v = v % 1440;
            const h = String(Math.floor(v / 60)).padStart(2, '0');
            const m = String(v % 60).padStart(2, '0');
            return h + ':' + m;
        },
        hhmmToMinutes(s) {
            if (!s || s.indexOf(':') === -1) return 0;
            const parts = s.split(':');
            const h = parseInt(parts[0]) || 0;
            const m = parseInt(parts[1]) || 0;
            return (((h * 60 + m) % 1440) + 1440) % 1440;
        },
        addStep() {
            this.form.steps.push({
                step_order: this.form.steps.length,
                delay_type: 'minutes',
                delay_value: 0,
                message: '',
                media_url: null,
                button_text: null,
                button_url: null,
                template_id: null,
                file: null
            });
        },
        removeStep(index) {
            this.form.steps.splice(index, 1);
            // Reorder steps
            this.form.steps.forEach((step, idx) => {
                step.step_order = idx;
            });
        },
        toLocalDatetime(millis) {
            // Convert unix millis to yyyy-MM-ddTHH:mm format for datetime-local input
            const d = new Date(millis);
            const pad = n => String(n).padStart(2, '0');
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
        },
        async saveFollowUp() {
            if (this.form.steps.length === 0) {
                this.$toast.error("Tambahkan minimal satu step");
                return;
            }

            // Convert expired_at_local to millis (or null if cleared)
            if (this.form.expired_at_local) {
                this.form.expired_at = new Date(this.form.expired_at_local).getTime();
            } else {
                this.form.expired_at = null;
            }

            this.isSaving = true;
            try {
                // Upload media files for each step
                for (let i = 0; i < this.form.steps.length; i++) {
                    const step = this.form.steps[i];
                    if (step.file) {
                        this.uploadingStepIndex = i;
                        const formData = new FormData();
                        formData.append('file', step.file);
                        const response = await this.$store.dispatch('cdn_upload', formData);
                        if (response && response.data && response.data.data && response.data.data.url) {
                            step.media_url = response.data.data.url;
                        }
                        step.file = null;
                    }
                }

                // Prepare data to send (remove file, previewUrl, and expired_at_local UI-only fields)
                // eslint-disable-next-line no-unused-vars
                const { expired_at_local, ...formData } = this.form;
                const dataToSend = {
                    ...formData,
                    steps: this.form.steps.map(step => ({
                        step_order: step.step_order,
                        delay_type: step.delay_type,
                        delay_value: step.delay_value,
                        message: step.message,
                        media_url: step.media_url,
                        button_text: step.button_text,
                        button_url: step.button_url,
                        template_id: step.template_id
                    }))
                };

                if (this.isEdit && this.followUpId) {
                    await this.$store.dispatch("followup/followup_update", {
                        id: this.followUpId,
                        ...dataToSend
                    });
                    this.$toast.success("Follow up berhasil diupdate");
                } else {
                    await this.$store.dispatch("followup/followup_create", dataToSend);
                    this.$toast.success("Follow up berhasil dibuat");
                }
                this.$router.push({ name: 'FollowUpList' });
            } catch (err) {
                this.$toast.error("Gagal menyimpan follow up");
            } finally {
                this.isSaving = false;
                this.uploadingStepIndex = null;
            }
        }
    }
};
</script>

